import React from 'react';
import { History, X, PlusCircle, MinusCircle, Edit3, ArrowLeftRight } from 'lucide-react';

export interface NodeDiffEntry {
  node_id: string;
  type: string;
  action: 'added' | 'removed' | 'modified';
  changed_fields?: string[];
}

interface RevisionDiffDrawerProps {
  isOpen: boolean;
  versionNo: number;
  diffs: NodeDiffEntry[];
  onClose: () => void;
  onRestore: (versionNo: number) => void;
}

export const RevisionDiffDrawer: React.FC<RevisionDiffDrawerProps> = ({
  isOpen,
  versionNo,
  diffs,
  onClose,
  onRestore,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-96 bg-background border-l border-border shadow-2xl z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm text-foreground">
            Revision v{versionNo} Diff
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground transition-colors"
          aria-label="Close diff drawer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5">
        {diffs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-xs">
            No structural differences detected from current draft.
          </div>
        ) : (
          diffs.map((diff) => (
            <div
              key={`${diff.node_id}-${diff.action}`}
              className="flex items-start gap-2.5 p-3 rounded-lg border border-border bg-card text-xs"
            >
              <DiffIcon action={diff.action} />
              <div className="flex flex-col gap-0.5 truncate flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{diff.type}</span>
                  <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded font-semibold tracking-wider">
                    {diff.action}
                  </span>
                </div>
                <span className="text-muted-foreground text-[11px] truncate">ID: {diff.node_id}</span>
                {diff.changed_fields && diff.changed_fields.length > 0 && (
                  <span className="text-muted-foreground text-[10px] mt-1">
                    Modified: {diff.changed_fields.join(', ')}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="p-4 border-t border-border flex items-center gap-3">
        <button
          type="button"
          onClick={() => onRestore(versionNo)}
          className="flex-1 min-h-[44px] flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm rounded-lg hover:bg-primary/90 transition-colors"
        >
          <ArrowLeftRight className="w-4 h-4" />
          <span>Restore Revision</span>
        </button>
      </div>
    </div>
  );
};

const DiffIcon: React.FC<{ action: NodeDiffEntry['action'] }> = ({ action }) => {
  if (action === 'added') {
    return <PlusCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />;
  }
  if (action === 'removed') {
    return <MinusCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />;
  }
  return <Edit3 className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />;
};
